import { useState } from 'react';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    vehicleType: '',
    service: '',
    city: '',
    phone: '',
    bestTime: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d4b78hcrg8gnguu95a6g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          vehicleType: '',
          service: '',
          city: '',
          phone: '',
          bestTime: '',
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-red-600 to-black text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-20 h-20 flex items-center justify-center mx-auto mb-6">
            <i className="ri-message-3-line text-6xl"></i>
          </div>
          <h1 className="text-5xl font-bold mb-4">Book Your Detail</h1>
          <p className="text-xl text-gray-200 max-w-2xl mx-auto">
            Text us to schedule or fill out the form below. We'll confirm your appointment via text!
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div>
              <h2 className="text-3xl font-bold text-black mb-6">Get In Touch</h2>
              
              {/* Text Booking */}
              <div className="bg-red-50 border-2 border-red-600 rounded-xl p-8 mb-8">
                <div className="w-16 h-16 flex items-center justify-center bg-red-600 text-white rounded-full mb-4">
                  <i className="ri-smartphone-line text-3xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-black mb-2">📱 Text to Book</h3>
                <p className="text-black mb-4">
                  To book, text 980-261-1286 and we'll give you a quote
                </p>
                <a 
                  href="sms:+19802611286" 
                  className="inline-block bg-red-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-black transition-colors cursor-pointer whitespace-nowrap"
                >
                  Text Us Now
                </a>
              </div>

              {/* Hours */}
              <div className="bg-gray-50 rounded-xl p-8 mb-8">
                <div className="w-16 h-16 flex items-center justify-center bg-black text-white rounded-full mb-4">
                  <i className="ri-time-line text-3xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-black mb-4">Business Hours</h3>
                <div className="space-y-2 text-black">
                  <p className="flex justify-between">
                    <span className="font-semibold">Saturday – Sunday:</span>
                    <span>10:00 AM – 6:30 PM</span>
                  </p>
                  <p className="flex justify-between text-red-600">
                    <span className="font-semibold">Monday – Friday:</span>
                    <span>Closed</span>
                  </p>
                </div>
              </div>

              {/* Service Area */}
              <div className="bg-gray-50 rounded-xl p-8">
                <div className="w-16 h-16 flex items-center justify-center bg-black text-white rounded-full mb-4">
                  <i className="ri-map-pin-line text-3xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-black mb-2">Service Area</h3>
                <p className="text-black">
                  Serving Charlotte, Mooresville, and anywhere around that area
                </p>
              </div>

              {/* Quick Links */}
              <div className="mt-8">
                <h3 className="text-xl font-bold text-black mb-4">Quick Links</h3>
                <div className="grid grid-cols-2 gap-3">
                  <a href="/services" className="text-red-600 hover:text-black font-semibold cursor-pointer whitespace-nowrap">
                    → Full Detail Packages
                  </a>
                  <a href="/services" className="text-red-600 hover:text-black font-semibold cursor-pointer whitespace-nowrap">
                    → Interior Packages
                  </a>
                  <a href="/services" className="text-red-600 hover:text-black font-semibold cursor-pointer whitespace-nowrap">
                    → Exterior Packages
                  </a>
                  <a href="/maintenance" className="text-red-600 hover:text-black font-semibold cursor-pointer whitespace-nowrap">
                    → Maintenance Plans
                  </a>
                </div>
              </div>
            </div>

            {/* Booking Message */}
            <div>
              <div className="bg-white border-2 border-gray-200 rounded-xl p-8 shadow-lg">
                <div className="text-center py-12">
                  <div className="w-24 h-24 flex items-center justify-center bg-red-600 text-white rounded-full mx-auto mb-6">
                    <i className="ri-message-3-fill text-5xl"></i>
                  </div>
                  <h2 className="text-4xl font-bold text-black mb-4">Ready to Book?</h2>
                  <p className="text-xl text-black mb-8">
                    To book, text <strong className="text-red-600">980-261-1286</strong> and we'll give you a quote
                  </p>
                  <a 
                    href="sms:+19802611286" 
                    className="inline-block bg-red-600 text-white px-10 py-4 rounded-lg font-semibold text-lg hover:bg-black transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Text Us Now
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
